import { Fragment, useMemo } from 'react';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { styled } from '@linaria/react';
import { css } from '@linaria/core';
import rgba from 'polished/lib/color/rgba';
import { List, Popover, Text, usePopoverOpen, zIndex, colors } from '@spaceshipapp/cambridge';
import Button from '../../components/Button';
import Link from '../../components/Link';
import logo from '../../images/logo.svg';
import { trans } from '../../utils/helpers';
import { Item, Profile } from './styled';
import useAuth from '../../contexts/auth';
import { referralPageAttempt } from '../../analytics/referral';
import Notification from '../../components/Notification';
import Trans from 'next-translate/Trans';

const REFERRAL_ENABLED = process.env.NEXT_PUBLIC_REFERRAL_ENABLED === 'TRUE';

const DesktopHeader: React.FC = () => {
  const { t } = useTranslation('common');
  const [{ authenticated }] = useAuth();
  const { pathname = '' } = useRouter() || {};
  const shouldShowPeakSeasonWarning = false;

  const isTrackingPage = useMemo(() => /^\/orders/.test(pathname), [pathname]);

  return (
    <Header>
      {shouldShowPeakSeasonWarning && (
        <Notification
          variant="warning"
          message={
            <Trans
              i18nKey={
                isTrackingPage
                  ? t('common:Tracking.peakSeasonWarning')
                  : t('common:ShipmentInfo.peakSeasonWarning')
              }
              components={{
                strong: <strong />,
                /* eslint-disable jsx-a11y/anchor-has-content */
                /* eslint-disable @typescript-eslint/naming-convention */
                1: (
                  <a
                    href={t('common:ShipmentInfo.peakSeasonWarningSupportUrl')}
                    target="_blank"
                    rel="noreferrer"
                    aria-label="Peak Season Support Article"
                  />
                ),
                /* eslint-enable jsx-a11y/anchor-has-content */
                /* eslint-enable @typescript-eslint/naming-convention */
              }}
            />
          }
        />
      )}
      <Nav className="container">
        <Logo>
          <Link
            href="/ship"
            aria-label={t('Header.logo')}
            data-tracking-label={t('commonEn:Header.logo')}
          >
            <img src={logo} alt={t('Header.logo')} width={173} height={32} />
          </Link>
        </Logo>
        <UL>
          <li>
            <Link href="/ship" data-tracking-label={t('commonEn:Header.shipping')}>
              {t('Header.shipping')}
            </Link>
          </li>
          <li>
            <Link href="/orders" data-tracking-label={t('commonEn:Header.tracking')}>
              {t('Header.tracking')}
            </Link>
          </li>
          {REFERRAL_ENABLED && (
            <li>
              <Link
                onClick={() => {
                  referralPageAttempt(authenticated);
                }}
                href="/referral"
                data-tracking-label={t('commonEn:Header.referral')}
              >
                {t('Header.referral')}
              </Link>
            </li>
          )}
        </UL>
        <BtnGroup>
          <Popover
            trigger={
              <Button
                iconName="Person"
                size="sm"
                variant="link"
                aria-label={t('Header.profile')}
                data-tracking-label={t('commonEn:Header.profile')}
              />
            }
          >
            <Dropdown />
          </Popover>
          <Popover
            trigger={
              <Button
                iconName="Globe"
                size="sm"
                variant="link"
                aria-label={t('Header.language')}
                data-tracking-label={t('commonEn:Header.language')}
              />
            }
          >
            <Languages />
          </Popover>
        </BtnGroup>
      </Nav>
    </Header>
  );
};

const Dropdown: React.FC = () => {
  const { t } = useTranslation('common');
  const [{ authenticated, user }] = useAuth();
  const togglePopover = usePopoverOpen();

  const handleClose = () => {
    togglePopover(false);
  };

  return (
    <Menu onClick={handleClose}>
      <List>
        {authenticated ? (
          <Fragment>
            <Item>
              <Profile>
                <Text className={usernameStyle}>{user?.name}</Text>
                <Text className="subtext" size="sm">
                  {user?.email}
                </Text>
              </Profile>
            </Item>
            <Item>
              <Link href="/logout" data-tracking-label={t('commonEn:Header.signOut')}>
                {t('Header.signOut')}
              </Link>
            </Item>
          </Fragment>
        ) : (
          <Fragment>
            <Item>
              <Link href="/login" data-tracking-label={t('commonEn:Header.signIn')}>
                {t('Header.signIn')}
              </Link>
            </Item>
            <Item>
              <Link href="/register" data-tracking-label={t('commonEn:Header.register')}>
                {t('Header.register')}
              </Link>
            </Item>
          </Fragment>
        )}
      </List>
    </Menu>
  );
};

const Languages: React.FC = () => {
  const { asPath, locales = [] } = useRouter();
  const togglePopover = usePopoverOpen();

  const handleClose = () => {
    togglePopover(false);
  };

  return (
    <Menu onClick={handleClose}>
      <List>
        {locales.map((locale) => (
          <Item key={locale}>
            <Link href={asPath} locale={locale}>
              {trans[locale]}
            </Link>
          </Item>
        ))}
      </List>
    </Menu>
  );
};

const Header = styled.header`
  position: sticky;
  z-index: ${zIndex.HEADER};
  top: 0;
  background-color: ${colors.brandSecondary};
  border-bottom: 1px solid ${colors.border};
`;

const Logo = styled.div`
  display: flex;
  align-items: center;

  > *:not(:first-child) {
    margin-left: 0.5rem;
  }
`;

const BtnGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;

  > *:not(:first-child) {
    margin-left: 0.5rem;
  }

  /* fix to allow menu align on the right */
  div[role='dialog'] {
    min-width: 20rem;
  }
`;

const Nav = styled.nav`
  display: grid;
  padding: 1.5em 0;
  grid-template-columns: 1fr auto 1fr;

  a {
    text-decoration: none;

    &.active {
      font-weight: 600;
    }
  }
`;

const UL = styled.ul`
  display: flex;
  align-items: center;

  > *:not(:first-child) {
    margin-left: 2.5rem;
  }
`;

const Menu = styled.div`
  border: solid 1px ${colors.border};
  box-shadow: 0 0.25em 0.75em ${rgba(colors.text, 0.08)};
  background-color: ${colors.surface};
  width: max(320px, 100%);
`;

const usernameStyle = css`
  font-weight: 600;
`;

export default DesktopHeader;
